import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Pie, PieChart, Cell } from "recharts";
import { TrendingUp } from "lucide-react";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../../../Components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../../../Components/ui/chart";
import { useSelector } from "react-redux";
import { RootState } from "@/src/State/store";

// Props interfaces
interface AudienceCount {
  fromCountry: string;
  toCountry: string;
  totalCount: number;
}

interface ApiData {
  status: string;
  status_Description: string;
  totalCount: number;
  audienceCounts: AudienceCount[];
}

interface ProcessedAudienceData extends AudienceCount {
  fromCountryName: string;
  toCountryName: string;
  fill: string;
}

interface AudienceCardProps {
  apiData: ApiData | null | undefined;
  isLoading?: boolean;
  audienceType: string; // ✅ add this
}

const AudienceCard = React.memo(function AudienceCard({
  apiData,
  isLoading,
  audienceType,
}: AudienceCardProps) {
  const data = apiData;

  const [countryNameMap, setCountryNameMap] = useState<Record<string, string>>(
    {}
  );
  const apiUrlAdvAcc = useSelector(
    (state: RootState) => state.authentication.apiURL
  );

  // Fetch country names from API
  useEffect(() => {
    if (!data?.audienceCounts) return;

    const uniqueCodes = Array.from(
      new Set(data.audienceCounts.flatMap((c) => [c.fromCountry, c.toCountry]))
    );

    const fetchNames = async () => {
      const map: Record<string, string> = {};
      await Promise.all(
        uniqueCodes.map(async (code) => {
          try {
            const queryParams = new URLSearchParams({ iso: code });
            const response = await axios.get(
              `${apiUrlAdvAcc}/GetCountryName?${queryParams.toString()}`
            );
            map[code] =
              response.data.status === "Success"
                ? response.data.countryName
                : code.toUpperCase();
          } catch {
            map[code] = code.toUpperCase();
          }
        })
      );
      setCountryNameMap(map);
    };

    fetchNames();
  }, [data, apiUrlAdvAcc]);

  // Utility: hash string to number
 // Utility: hash string to number
function stringToNumber(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

// ✅ Generate full-spectrum unique HSL color
function stringToColor(str: string): string {
  const hash = stringToNumber(str);

  // Spread hue across the full 0–360 range
  const h = hash % 360;

  // Keep saturation and lightness in a readable range
  const s = 50 + (hash % 40); // 50–90%
  const l = 35 + (hash % 40); // 35–75%

  return `hsl(${h}, ${s}%, ${l}%)`;
}

// Process API data with colors and fetched country names
const audienceData: ProcessedAudienceData[] = useMemo(() => {
  if (!data?.audienceCounts) return [];

  const filtered = data.audienceCounts.filter((item) => item.totalCount > 0);

  return filtered.map((item) => ({
    ...item,
    fromCountryName:
      countryNameMap[item.fromCountry] || item.fromCountry.toUpperCase(),
    toCountryName:
      countryNameMap[item.toCountry] || item.toCountry.toUpperCase(),
    fill: stringToColor(item.fromCountry), // 🔹 Now uses full-spectrum colors
  }));
}, [data?.audienceCounts, countryNameMap]);

  const totalCount = data?.totalCount || 0;

  const chartConfig: ChartConfig = {
    totalCount: { label: "Recipients" },
    ...audienceData.reduce((acc, item) => {
      acc[item.fromCountryName] = {
        label: item.fromCountryName,
        color: item.fill,
      };
      return acc;
    }, {} as ChartConfig),
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center h-64">
          <p className="text-muted-foreground">Loading audience data...</p>
        </CardContent>
      </Card>
    );
  }

  if (!audienceData.length || totalCount === 0) {
    return (
      <Card>
        <CardHeader className="items-center pb-0">
          <CardTitle>Audience Size</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No audience data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col max-h-[550px]">
      <CardHeader className="items-center pb-0">
        <CardTitle>Audience Size</CardTitle>
      </CardHeader>

      <CardContent className="flex-1 pb-0 overflow-visible">
        <ChartContainer config={chartConfig} className="mx-auto max-h-[300px]">
          <PieChart
            className="mt-4"
            width={650}
            height={300}
            margin={{ top: 10, right: 20, bottom: 10, left: 20 }}
          >
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
            <Pie
              data={audienceData}
              dataKey="totalCount"
              nameKey="fromCountryName"
              cx="50%"
              cy="50%"
              innerRadius={60} // slightly smaller inner radius
              outerRadius={90} // increase outer radius to fill space
              strokeWidth={3}
              label={false}
              labelLine={false}
            >
              {audienceData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>

        {/* 🔹 Scrollable Legend Below Chart */}
        <div className="mt-6 mb-6 max-h-[100px] overflow-y-auto  pl-8">
          <div className="flex flex-wrap gap-4">
            {audienceData.map((item, index) => (
              <div
                key={index}
                className="flex items-center space-x-2 min-w-[120px]"
              >
                <div
                  className="w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: item.fill }}
                />
                <span className="text-sm font-medium text-[#1C2024] truncate">
                  {item.fromCountryName} ({item.totalCount.toLocaleString()})
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>

      <CardFooter className="flex-col gap-2 mt-[-20px]">
        <div className="w-full px-2">
          <div className="mt-4 text-center text-[14px] text-[#1C2024] font-bold">
            {totalCount.toLocaleString()}{" "}
{audienceType === "Reuse Audience"
  ? "Total Eligible Recipients"
  : "Total Estimated Recipients"}
          </div>
          <div className="flex px-2 items-start mt-4 pt-2">
            <TrendingUp
              className="text-[#64748B] text-[10px] mt-[2px] mr-2 flex-shrink-0"
              size={10}
            />
            <p className="text-[10px] font-medium text-[#64748B] text-justify leading-relaxed">
              The accuracy of estimates is based on factors such as past
              campaign data and market data. These estimates are provided to
              give you an idea of performance but are only estimates and don't
              guarantee results.
            </p>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
});

export default AudienceCard;
